import BackgroundHue from '@atoms/BackgroundHue/BackgroundHue';
import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import MotionWrapper from '@atoms/MotionWrapper/MotionWrapper';
import GlowColourEnum from '@models/enums/GlowColourEnum';
import type { IImage } from '@models/IImage';
import type { ILink } from '@models/ILink';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useReducedMotion from '@utils/hooks/useReducedMotion';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import { useSpringNumber } from '@utils/hooks/useSpring';
import classNames from 'classnames';
import { useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';

interface Props {
  readonly title: string;
  readonly subTitle: string;
  readonly mainImage: IImage;
  readonly secondaryImage?: IImage;
  readonly cta?: ILink;
  readonly glow: boolean;
}

const SimplifiedProductImageGallery = ({
  title,
  subTitle,
  mainImage,
  secondaryImage,
  cta,
  glow,
}: Props) => {
  const prefersReducedMotion = useReducedMotion();
  const isSmallDesktop = useMediaQuery(1440);
  const isMobile = useMediaQuery(1024);
  const [
    mainImageContainerRef,
    { height: mainImageContainerHeight, width: mainImageContainerWidth },
  ] = useResizeObserver();
  const mediaContainerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: mediaContainerRef,
    offset: ['start end', 'end start'],
  });
  const smoothScrollYProgress = useSpringNumber(scrollYProgress, {
    stiffness: 300,
    damping: 50,
  });
  const mainImageTranslateYLimit = 60;
  const mainImageTranslateY = useTransform(
    smoothScrollYProgress,
    [0, 0.5, 1],
    [mainImageTranslateYLimit, 0, -mainImageTranslateYLimit]
  );
  const secondaryImageTranslateYLimit = isSmallDesktop ? 90 : 120;
  const secondaryImageTranslateY = useTransform(
    smoothScrollYProgress,
    [0, 0.5, 1],
    [secondaryImageTranslateYLimit, 0, -secondaryImageTranslateYLimit]
  );
  const hasTwoImages = !!mainImage && !!secondaryImage;

  return (
    <section
      className="component-padding overflow-hidden bg-white pt-13 text-black-100 lg:pt-[110px] dark:bg-black-100 dark:text-white"
      data-testid="CT-14-A-Simplified-Product-Image-Gallery"
    >
      <SoftLoadWrapper>
        <div
          className="container flex grid-cols-12 flex-col xl:grid"
          ref={mediaContainerRef}
        >
          <div
            className="relative z-10 flex flex-col justify-center gap-[15px] max-lg:pb-16 max-xl:items-center lg:gap-10 xl:col-span-5"
            data-testid="CT-14-A-Simplified-Product-Image-Gallery-Text-Container"
          >
            <h1
              className="mount max-xl:text-center"
              data-testid="CT-14-A-Simplified-Product-Image-Gallery-Title"
            >
              {title}
            </h1>
            <p
              className={classNames(
                'text-grey-450 max-xl:text-center dark:text-grey-400',
                isMobile ? 'h5' : 'h3'
              )}
              data-testid="CT-14-A-Simplified-Product-Image-Gallery-Subtitle"
            >
              {subTitle}
            </p>
            {cta?.name && cta.url && (
              <Button
                link={cta}
                className="mt-2.5 w-fit xl:mt-4"
                dataTestId="CT-14-A-Simplified-Product-Image-Gallery-CTA"
              />
            )}
          </div>
          <div
            className={classNames(
              'flex justify-end xl:col-span-6 xl:col-start-8',
              { relative: hasTwoImages }
            )}
            data-testid="CT-14-A-Simplified-Product-Image-Gallery-Media-Container"
            ref={mainImageContainerRef}
          >
            <MotionWrapper
              style={{
                ...(hasTwoImages
                  ? {
                      translateY: prefersReducedMotion
                        ? '0px'
                        : mainImageTranslateY,
                    }
                  : {}),
              }}
              className={classNames({
                'relative max-xl:w-[85%]': hasTwoImages,
              })}
            >
              <Image
                image={mainImage}
                sizes="(min-width: 1024px) 1024px, 100vw"
                dataTestId="CT-14-A-Simplified-Product-Image-Gallery-Main-Image"
              />

              {hasTwoImages && glow && (
                <BackgroundHue
                  colour={GlowColourEnum.Yellow}
                  motionStyle={{
                    height: mainImageContainerHeight,
                    width: mainImageContainerWidth,
                    translateY: prefersReducedMotion
                      ? '0px'
                      : mainImageTranslateY,
                    scale: 2.5,
                    opacity: 0.35,
                  }}
                  className="absolute bottom-0 -z-max max-h-full xl:-bottom-20 xl:-left-36"
                  dataTestId="CT-14-A-Simplified-Product-Image-Gallery-Glow-Image"
                />
              )}
            </MotionWrapper>
          </div>

          {hasTwoImages && (
            <MotionWrapper
              style={{
                translateY: prefersReducedMotion
                  ? '0px'
                  : secondaryImageTranslateY,
                zIndex: 3,
              }}
              className="relative xl:col-span-2 xl:col-start-7"
            >
              <div className="absolute -bottom-5 left-0 flex max-h-[86px] max-w-[114px] md:max-h-[180px] md:max-w-[240px] xl:-bottom-20 xl:max-h-[390px] xl:max-w-[470px]">
                <Image
                  image={secondaryImage}
                  imageClasses="h-full object-contain"
                  dataTestId="CT-14-A-Simplified-Product-Image-Gallery-Main-Image"
                />
              </div>
            </MotionWrapper>
          )}
        </div>
      </SoftLoadWrapper>
    </section>
  );
};

export default SimplifiedProductImageGallery;
